<template>
  <div class="base-details">
    <slot name="title">
      <h3 v-if="title" class="sm:text-base text-sm font-bold sm:mb-2.5">
        {{ title }}
      </h3>
    </slot>

    <div v-if="content && content.trim().length > 0">
      <p :class="[contentClass, 'whitespace-pre-line']">
        {{ displayText }}<span v-if="shouldTruncate && !isExpanded">...</span>
      </p>

      <button
        v-if="shouldTruncate"
        @click="isExpanded = !isExpanded"
        type="button"
        class="text-primary-blue sm:text-[13px] text-[11px] font-normal cursor-pointer mt-1 inline-block"
      >
        {{
          isExpanded
            ? $t("artist_details.show_less")
            : $t("artist_details.show_more")
        }}
      </button>
    </div>

    <slot v-else name="empty">
      <p :class="emptyClass">{{ emptyText }}</p>
    </slot>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";

interface Props {
  title?: string;
  content?: string;
  threshold?: number;
  emptyText?: string;
  contentClass?: string;
  emptyClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  title: "",
  content: "",
  threshold: 250,
  emptyText: "No details available",
  contentClass: "leading-relaxed max-w-3xl text-gray-700",
  emptyClass: "text-sm italic text-gray-500",
});

const isExpanded = ref(false);

const shouldTruncate = computed(() => {
  return props.content.length > props.threshold;
});

const displayText = computed(() => {
  if (!shouldTruncate.value || isExpanded.value) {
    return props.content;
  }
  return props.content.slice(0, props.threshold);
});
</script>
